import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { useSnackbar } from 'notistack'
import { Link as RouterLink } from 'react-router-dom'
import { colors } from 'src/components/app-ui'
import URL from 'src/api/url/api'
import UserType from 'src/model/user-types'
import { hasInAppAuthAccess } from 'src/components/user-access/utils/in-app-auth-access'
import safeTrack from 'src/utils/safe-track'

const GUIDE_URL =
  'https://intercom.help/mini-course-generator/en/articles/9403804-enable-access-with-in-app-authentication'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  head: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: colors.navy,
  },
  tag: {
    fontSize: 11,
    fontWeight: 500,
    color: colors.muted,
    background: colors.hover,
    padding: '2px 8px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  desc: {
    fontSize: 14,
    color: colors.muted,
    lineHeight: 1.55,
    maxWidth: 820,
  },
  guide: {
    alignSelf: 'flex-start',
    fontSize: 14,
    fontWeight: 500,
    color: colors.primary,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  steps: {
    background: '#FFFFFF',
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: '8px 24px',
  },
  step: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '18px 0',
    '& + &': { borderTop: '1px solid #F1F3F5' },
  },
  number: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: colors.primarySoft,
    color: colors.primary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flex: 'none',
  },
  stepBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.navy,
  },
  codeWrap: {
    position: 'relative',
  },
  // Long lines scroll sideways instead of wrapping.
  code: {
    margin: 0,
    background: colors.hover,
    borderRadius: 10,
    padding: '16px 20px',
    paddingRight: 120,
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: 13,
    lineHeight: 1.7,
    color: colors.navy,
    whiteSpace: 'pre',
    overflowX: 'auto',
  },
  copy: {
    position: 'absolute',
    top: 10,
    right: 10,
    background: '#FFFFFF',
    color: colors.navy,
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: 6,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    '&:hover': { background: colors.hover },
  },
  note: {
    fontSize: 13,
    color: colors.muted,
    lineHeight: 1.55,
  },
  locked: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '48px 24px',
    textAlign: 'center',
    background: '#FFFFFF',
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
  },
  lockedTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.navy,
  },
  lockedLink: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.primary,
    textDecoration: 'none',
  },
})

// The gateway of this environment (test or production), as the API calls use.
const gatewayBase = () =>
  String(URL.API || 'https://gateway.minicoursegenerator.com/api/').replace(
    /\/+$/,
    '',
  )

const serverCode = ({ portalId, secretKey, shopId }) =>
  `var CryptoJS = require("crypto-js");
var portalId = "${portalId}";
var secretKey = CryptoJS.enc.Utf8.parse("${secretKey}");

function encryptData(data) {
  return encodeURIComponent(CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(JSON.stringify(data)), secretKey,
    { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 }).toString());
}

var token = encryptData({
  courseCollectionId: portalId,
  username: "user_123",
  email: "learner@company.com", // optional
  name: "Jane Doe" // optional
});

var magicLink = "${gatewayBase()}/learner-auth/${shopId}/generate-magic-link?token=" + token;`

const IFRAME_CODE =
  '<iframe src="MAGIC_LINK" width="100%" height="100%" style="border:0" allowfullscreen></iframe>'

const CodeBlock = ({ code, block }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const copy = () => {
    safeTrack('copied_portal_inapp_code', { block })
    try {
      navigator.clipboard?.writeText(code)
    } catch (error) {
      // Clipboard blocked: nothing else to do.
    }
    enqueueSnackbar('Copied', { variant: 'success' })
  }
  return (
    <div className={classes.codeWrap}>
      <pre className={classes.code}>{code}</pre>
      <button type="button" className={classes.copy} onClick={copy}>
        <Icon icon="tabler:copy" width={13} height={13} />
        Copy code
      </button>
    </div>
  )
}

/**
 * Portal › In-app authentication: how to embed the portal in the customer's
 * product, so learners signed in there open it without a second login. Same
 * signed magic link as a path's in-app authentication, for the portal
 * (showcase) collection; it opens the portal's page without the portal's
 * navigation bar and footer.
 */
const InAppTab = ({ collection }) => {
  const classes = useStyles()
  const constants = window.CONSTANTS || {}
  // Without in-app authentication on the plan, the code (and the secret key in it) isn't shown.
  // The plans a path's in-app authentication allows (Learners' Access).
  const roleName = UserType.getRoleName(constants.USER_ROLE_ID)
  const allowed = hasInAppAuthAccess(roleName) || roleName === 'Super Admin'
  const code = serverCode({
    portalId: collection?.id || '',
    secretKey: constants.SECRET_KEY || '',
    shopId: constants.SHOP_ID || '',
  })

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <div className={classes.titleRow}>
          <div className={classes.title}>
            Embed your portal with in-app authentication
          </div>
          <span className={classes.tag}>Developer</span>
        </div>
        <div className={classes.desc}>
          Learners who are signed in to your product open your portal without a
          second login. Your developer creates a signed link on your server and
          embeds it in your product. The embedded portal has no navigation bar
          or footer.
        </div>
        <a
          className={classes.guide}
          href={GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the guide
        </a>
      </div>

      {!allowed && (
        <div className={classes.locked}>
          <Icon
            icon="tabler:lock"
            width={32}
            height={32}
            color={colors.subtle}
          />
          <div className={classes.lockedTitle}>
            In-app authentication is available on the Headless plans
          </div>
          <RouterLink to="/pricing" className={classes.lockedLink}>
            See plans
          </RouterLink>
        </div>
      )}

      {allowed && (
        <div className={classes.steps}>
          <div className={classes.step}>
            <span className={classes.number}>1</span>
            <div className={classes.stepBody}>
              <div className={classes.stepTitle}>
                Create a magic link on your server
              </div>
              <CodeBlock code={code} block="server_code" />
              <div className={classes.note}>
                This code contains your secret key. Run it on your server, never
                in front-end code. The link opens the portal without navigation
                bar and footer, and signs the learner in.
              </div>
            </div>
          </div>
          <div className={classes.step}>
            <span className={classes.number}>2</span>
            <div className={classes.stepBody}>
              <div className={classes.stepTitle}>Embed it in your product</div>
              <CodeBlock code={IFRAME_CODE} block="iframe" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InAppTab
